/**
 * Setup-password page (visited via the URL the admin CLI mints, e.g.
 * `/auth/setup?token=abc...`).
 *
 * Flow:
 *   1. Read `token` from query string. If missing → show error.
 *   2. GET /api/auth/setup-info?token=... to confirm the token resolves
 *      and to display the matching username/email.
 *   3. User enters password + confirm. On submit → POST /api/auth/setup,
 *      then automatically log in via AuthProvider and navigate to /.
 */

import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ApiError, fetchJson, postJson } from "../../api/client";
import { useAuth } from "../../auth/AuthProvider";
import type { SetupInfo } from "../../api/types";

const MIN_PW = 8;

export default function SetupPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { login } = useAuth();

  const [info, setInfo] = useState<SetupInfo | null>(null);
  const [infoError, setInfoError] = useState<string | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setInfoError("No setup token in URL.");
      setLoadingInfo(false);
      return;
    }
    let cancelled = false;
    fetchJson<SetupInfo>(
      `/api/auth/setup-info?token=${encodeURIComponent(token)}`,
    )
      .then((res) => {
        if (!cancelled) setInfo(res);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setInfoError("This setup link has expired or already been used.");
        } else if (err instanceof ApiError) {
          setInfoError(err.detail ?? err.message);
        } else {
          setInfoError("Couldn't validate the setup link.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingInfo(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);

    if (password.length < MIN_PW) {
      setSubmitError(`Password must be at least ${MIN_PW} characters.`);
      return;
    }
    if (password !== confirm) {
      setSubmitError("Passwords don't match.");
      return;
    }

    setSubmitting(true);
    try {
      await postJson<{ ok: true }>("/api/auth/setup", { token, password });
      // Backend's /api/auth/setup doesn't auto-login; do it ourselves so
      // the user lands on / authenticated.
      if (info) {
        await login({ login: info.username, password });
      }
      navigate("/", { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        const fieldErr = err.errors?.password;
        setSubmitError(fieldErr ?? err.detail ?? err.message);
      } else {
        setSubmitError("Couldn't set the password. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingInfo) {
    return (
      <main className="auth-shell">
        <section className="auth-card">
          <p className="empty-state">Loading…</p>
        </section>
      </main>
    );
  }

  if (infoError || !info) {
    return (
      <main className="auth-shell">
        <section className="auth-card">
          <h1 className="auth-card__title">Setup link invalid</h1>
          <p className="auth-card__subtitle">
            {infoError ?? "Please request a new link."}
          </p>
          <div className="form-actions">
            <button
              type="button"
              className="button button--primary"
              onClick={() => navigate("/login", { replace: true })}
            >
              Go to sign-in
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className="auth-shell">
      <section className="auth-card">
        <h1 className="auth-card__title">Set your password</h1>
        <p className="auth-card__subtitle">
          Welcome <strong>{info.username}</strong>. Pick a password to finish
          setting up your account.
        </p>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="field">
            <label className="field__label" htmlFor="setup-pw">
              New password
            </label>
            <input
              id="setup-pw"
              className="field__input"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PW}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="setup-pw-confirm">
              Confirm password
            </label>
            <input
              id="setup-pw-confirm"
              className="field__input"
              type="password"
              autoComplete="new-password"
              required
              minLength={MIN_PW}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>

          {submitError && (
            <p className="form-status" data-variant="error" role="alert">
              {submitError}
            </p>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="button button--primary"
              disabled={submitting || !password || !confirm}
            >
              {submitting ? "Saving…" : "Set password & sign in"}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}
